import type { NameValueObject } from "@neru/types";
import { isNotEmpty, toNameValueObject } from "@neru/utils";
import { useRef } from "react";

import { Button } from "../Button/Button";
import { Checkbox } from "../Checkbox/Checkbox";
import { listItemStyles, Menu, type MenuMethods } from "../Menu/Menu";
import { Row } from "../Row/Row";

type Option<T> = NameValueObject<T> | T;

interface DropdownProps<T> {
  "data-testid"?: string;
  initFormElements?: (children: React.ReactNode) => React.ReactNode;
  items: Array<Option<T> | null | undefined>;
  label?: string;
  name: string;
  selectedValues?: Array<Option<T>>;
}

export const Dropdown = <T extends string | number>({
  "data-testid": testId = "dropdown",
  initFormElements = (el) => el,
  items,
  label = "Select options",
  name,
  selectedValues = [],
}: DropdownProps<T>) => {
  const menu = useRef<MenuMethods>(null);

  const normalizedItems = items.filter(isNotEmpty).map(toNameValueObject);

  const selectedValuesArray = (
    Array.isArray(selectedValues) ? selectedValues : [selectedValues]
  )
    .map(toNameValueObject)
    .map(({ value }) => value.toString());

  return (
    <Menu
      data-testid={testId}
      icon="chevron-down-line"
      label={label}
      layout="outlined"
      ref={menu}
    >
      <div
        className={`
          bg-primary-0 py-2 text-primary-1000

          dark:bg-primary-950 dark:text-primary-0
        `}
      >
        <div className="max-h-[400px] overflow-scroll">
          {initFormElements(
            normalizedItems.map(
              ({ name: itemName, value }: NameValueObject<T>) => (
                <div className={listItemStyles} key={value}>
                  <Checkbox
                    checked={selectedValuesArray.includes(value.toString())}
                    data-testid={`${testId}-option-${value}`}
                    label={String(itemName)}
                    name={name}
                    value={String(value)}
                  />
                </div>
              ),
            ),
          )}
        </div>
        <Row className="w-max justify-end whitespace-nowrap px-4 pt-2">
          <Button
            label="Done"
            layout="filled"
            onClick={() => menu.current?.close()}
            size="condensed"
            type="button"
          />
        </Row>
      </div>
    </Menu>
  );
};
